<template>
  <div class="container main-container main-container--accordion">
    <!-- START USER EVALUATIONS -->
    <div class="row">
      <div
        class="col-lg-16 evaluations__top-left d-inline-flex justify-content-between align-items-center"
      >
        <h2 class="h1 h1--evaluations">
          <span>{{ 'My Evaluations' | translate }}</span>
        </h2>
      </div>
      <div
        class="col-lg-8 d-flex align-items-center justify-content-lg-end flex-column flex-md-row"
      >
        <router-link
          to="/quick-start/"
          class="btn btn-primary btn-primary--thin btn-primary--thin--long btn-full--sm ml-0 ml-xl-2"
          :class="{
            'disabled-btn': user.role === 'intro' && allEvaluations.length >= 5,
          }"
          @click.native.prevent="
            user.role === 'intro' && allEvaluations.length >= 5
              ? null
              : $router.push('/quick-start/')
          "
        >
          {{ 'Quick Start Evaluation' | translate }}
        </router-link>
        <router-link
          to="/evaluations/new"
          class="btn btn-primary btn-primary--thin btn-primary--thin--short btn-full--sm ml-md-2 mt-2 mt-md-0"
          :class="{
            'disabled-btn': user.role === 'intro' && allEvaluations.length >= 5,
          }"
          @click.native.prevent="
            user.role === 'intro' && allEvaluations.length >= 5
              ? null
              : $router.push('/evaluations/new')
          "
        >
          {{ 'New Evaluation' | translate }}
        </router-link>
      </div>
    </div>

    <hr class="my-4 d-lg-none" />

    <evaluation-list :user="user" isShared="no"></evaluation-list>
    <!-- END USER EVALUATIONS -->

    <div v-if="user.role === ROLES.zinpro_representative">
      <div class="row mt-5">
        <div
          class="col-lg-16 evaluations__top-left d-inline-flex justify-content-between align-items-center"
        >
          <h2 class="h1 h1--evaluations">
            <span>{{ 'Shared Evaluations' | translate }}</span>
          </h2>
        </div>
        <div class="col-lg-8">
          <select class="form-control" v-model="sharedCustomerId">
            <option selected :value="null">
              {{ 'Select' | translate }}
            </option>
            <option v-for="(customer, index) in sharedUsers" :key="index" :value="customer._id">
              {{ customer.firstName }} {{ customer.lastName }}
            </option>
          </select>
        </div>
      </div>
      <evaluation-list :user="user" :customerId="sharedCustomerId" isShared="yes"></evaluation-list>
    </div>

    <!-- START CUSTOMER EVALUATIONS -->
    <div v-show="user.role === ROLES.zinpro_representative">
      <div class="row mt-5">
        <div
          class="col-lg-16 evaluations__top-left d-inline-flex justify-content-between align-items-center"
        >
          <h2 class="h1 h1--evaluations">
            <span>{{ 'Customer Evaluations' | translate }}</span>
          </h2>
        </div>
        <div class="col-lg-8">
          <select class="form-control" v-model="customerId" @change="changeCustomer(customerId)">
            <option selected disabled :value="null">
              {{ 'Select' | translate }}
            </option>
            <option v-for="(customer, index) in user.customers" :key="index" :value="customer.id">
              {{ customer.name }} ({{ $store.getters.getRoleName(customer.role) | translate }})
            </option>
          </select>
        </div>
      </div>
      <hr class="my-4 d-lg-none" />
      <div v-if="customer === null">
        <div class="mt5 text-center">
          <h5 class="text-uppercase">
            {{ 'You have not selected a customer.' | translate }}
          </h5>
        </div>
      </div>
      <evaluation-list v-else :customerId="customer._id" isShared="no"></evaluation-list>
      <!-- END CUSTOMER EVALUATIONS -->
    </div>

    <!-- Informational Modal for Intro users with 3 evaluations -->
    <div
      v-if="showIntroLimitModal"
      class="modal fade show"
      style="display: block"
      tabindex="-1"
      role="dialog"
      aria-labelledby="introLimitModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog--edit" role="document">
        <div class="modal-content edit-modal pt-0 px-0">
          <div class="modal-body my-0 py-0 px-0">
            <h2 class="h2 pt-3 pb-2 px-3 text-center">
              {{ 'Thank you for your interest in Zinpro® FirstStep®!' | translate }}
            </h2>
            <p class="px-3 mb-4">
              {{
                'You have completed your limited trial access to the program. To upgrade your account to unlimited Evaluations and the complete set of Assessors, please contact your Zinpro® Representative.'
                  | translate
              }}
            </p>
            <div class="px-3 mb-4 text-center">
              <a
                href="https://www.zinpro.com/contact-sales/?_expert_type=representative"
                target="_blank"
                class="btn btn-secondary"
              >
                {{ 'Contact Zinpro Representative' | translate }}
              </a>
            </div>
            <div class="row modal-bottom pb-3">
              <button @click="closeIntroLimitModal" class="btn btn-secondary">
                {{ 'OK' | translate }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showIntroLimitModal" class="modal-backdrop fade show"></div>

    <!-- Share evaluation -->
  </div>
</template>

<script>
import Vue from 'vue';
import _ from 'lodash';
import EvaluationList from '@/pages/evaluations/EvaluationList.vue';
import ShareEvaluationList from '@/pages/evaluations/ShareEvaluationList.vue';
import { ROLES } from '../../constants';
import { mapActions, mapState } from 'vuex';
import {
  GET_CUSTOMER_BY_ID,
  GET_USER_BY_ID,
  SET_UPDATE_LOGIN_USER,
  GET_EVALUATION_SHARE_WITH_ME,
} from '../../store/types';

export default {
  components: {
    EvaluationList,
    ShareEvaluationList,
  },
  data() {
    return {
      customer: null,
      customerId: null,
      sharedCustomerId: null,
      ROLES,
      showIntroLimitModal: false,
      evaluationsLoaded: false,
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.users.user,
      users: (state) => state.users.users,
      allEvaluations: (state) => state.evaluations.evaluations,
      sharedUsers: (state) => state.evaluations.sharedEvaluationsUsers,
    }),
    shouldShowIntroLimitModal() {
      return (
        this.user &&
        this.user.role === 'intro' &&
        this.allEvaluations &&
        this.allEvaluations.length >= 5
      );
    },
  },
  methods: {
    ...mapActions('users', [GET_CUSTOMER_BY_ID, GET_USER_BY_ID]),
    async changeCustomer(customerId) {
      this.customer = this.users.find((user) => user && user._id === this.customerId);
      if (!this.customer) this.customer = await this[GET_CUSTOMER_BY_ID](this.customerId);
    },

    displayError(errorCode) {
      console.log('errorCode', errorCode);
      if (errorCode === 'evaluation/not-found') {
        this.errorMessage = 'Sorry! We can not find that evaluation at this time.';
      } else {
        this.errorMessage = 'Sorry! Something went wrong.';
      }
    },
    closeIntroLimitModal() {
      this.showIntroLimitModal = false;
    },
    checkAndShowIntroLimitModal() {
      // Only show modal after evaluations have been loaded from API
      // This prevents showing stale data during impersonation
      if (this.evaluationsLoaded && this.shouldShowIntroLimitModal) {
        this.showIntroLimitModal = true;
      }
    },
  },
  async created() {
    // const user = await this[GET_USER_BY_ID](this.user._id);
    // this.$store.commit(`users/${SET_UPDATE_LOGIN_USER}`, user);

    if (this.$route.query.errorCode !== undefined) {
      this.displayError(this.$route.query.errorCode);
    }
  },
  mounted() {
    this.checkAndShowIntroLimitModal();
  },
  watch: {
    // Watch for changes in user or evaluations to show modal when returning to evaluations page
    user: {
      handler() {
        this.checkAndShowIntroLimitModal();
      },
      deep: true,
    },
    allEvaluations: {
      handler(newEvaluations, oldEvaluations) {
        // Mark evaluations as loaded when they change from the API
        // This prevents showing modal with stale cached data
        if (newEvaluations !== oldEvaluations) {
          this.evaluationsLoaded = true;
        }
        this.checkAndShowIntroLimitModal();
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.disabled-btn {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
  background-color: #ccc !important;
  border-color: #ccc !important;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1040;
}

.modal.show {
  z-index: 1050;
}
</style>
